import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getSprintDados } from "../api/sprint.api";
import { getSetupSprint } from "../api/setup.api";

const REFRESH_SEC = 30;

const FECHADOS = new Set([
  "Fechado", "Resolvido", "Reteste", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
]);

const isProg = t =>
  t.programado === "1" || t.programado === 1 || t.programado === true ||
  t.programacao_tipo === 1 || t.programacao_tipo === 2;

const SIZE_H = { P: 1, M: 9, G: 18, XG: 36 };
function ticketH(t) {
  return SIZE_H[String(t.size || "").toUpperCase().trim()] ?? 0;
}

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "nelson",    bg: "#1C5040", color: "#A0DFC4" },
];
function devAv(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const l = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (l.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function devInitials(nome) {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
}

function parseDateLocal(str) {
  if (!str) return null;
  const [d] = str.split(" ");
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

const STATUS_CFG = {
  concluido: { color: "#4ade80", label: "CONCLUÍDO",  icon: "✓", pulse: false },
  ok:        { color: "#4ade80", label: "NO PRAZO",   icon: "✓", pulse: false },
  risco:     { color: "#fbbf24", label: "EM RISCO",   icon: "⚠", pulse: true  },
  atrasado:  { color: "#f87171", label: "ATRASADO",   icon: "✕", pulse: true  },
};

function calcSaude(tickets, sprint) {
  const hoje   = new Date(); hoje.setHours(0, 0, 0, 0);
  const inicio = parseDateLocal(sprint?.data_inicial);
  const fim    = parseDateLocal(sprint?.data_final);
  if (!inicio || !fim) return null;

  const totalDias     = Math.max(1, Math.round((fim - inicio) / 86400000));
  const diasPassados  = Math.max(0, Math.min(totalDias, Math.round((hoje - inicio) / 86400000)));
  const diasRestantes = Math.max(0, Math.round((fim - hoje) / 86400000));
  const pctDias       = Math.round(diasPassados / totalDias * 100);

  const prog         = tickets.filter(isProg);
  const total        = prog.length;
  const fechadosList = prog.filter(t => FECHADOS.has(t.status));
  const fechados     = fechadosList.length;
  const abertos      = total - fechados;
  const pctConc      = total > 0 ? Math.round(fechados / total * 100) : 0;

  // Hours from Size field (0 when Size not set)
  const horasTotais   = prog.reduce((s, t) => s + ticketH(t), 0);
  const horasFechadas = fechadosList.reduce((s, t) => s + ticketH(t), 0);
  const horasAbertos  = horasTotais - horasFechadas;
  const pctHoras      = horasTotais > 0 ? Math.round(horasFechadas / horasTotais * 100) : 0;
  const usarHoras     = horasTotais > 0;

  // Velocity: h/dia when Size data available, tickets/dia otherwise
  const unidadeFechadas  = usarHoras ? horasFechadas : fechados;
  const unidadeAbertos   = usarHoras ? horasAbertos  : abertos;
  const velAtual         = diasPassados > 0 ? unidadeFechadas / diasPassados : 0;
  const velNecessaria    = diasRestantes > 0 && unidadeAbertos > 0 ? unidadeAbertos / diasRestantes : 0;

  const ratio = velNecessaria > 0
    ? velAtual / velNecessaria
    : unidadeAbertos === 0 ? 2 : 0;

  const diasParaConcluir = velAtual > 0 && unidadeAbertos > 0
    ? Math.ceil(unidadeAbertos / velAtual)
    : unidadeAbertos === 0 ? 0 : null;
  const diasAlem = diasParaConcluir != null ? diasParaConcluir - diasRestantes : null;

  const status = unidadeAbertos === 0 ? "concluido"
    : diasPassados === 0              ? "ok"
    : ratio >= 1.0                    ? "ok"
    : ratio >= 0.65                   ? "risco"
    : "atrasado";

  return {
    total, fechados, abertos, pctConc,
    horasTotais, horasFechadas, horasAbertos, pctHoras, usarHoras,
    diasPassados, diasRestantes, totalDias, pctDias,
    velAtual, velNecessaria, ratio,
    diasParaConcluir, diasAlem, status,
  };
}

function calcDevSaude(devTickets, diasPassados, diasRestantes) {
  const total        = devTickets.length;
  const fechadosList = devTickets.filter(t => FECHADOS.has(t.status));
  const fechados     = fechadosList.length;
  const abertos      = total - fechados;

  const horasTotais   = devTickets.reduce((s, t) => s + ticketH(t), 0);
  const horasFechadas = fechadosList.reduce((s, t) => s + ticketH(t), 0);
  const horasAbertos  = horasTotais - horasFechadas;
  const usarHoras     = horasTotais > 0;

  const unidadeFechadas = usarHoras ? horasFechadas : fechados;
  const unidadeAbertos  = usarHoras ? horasAbertos  : abertos;
  const velAtual = diasPassados > 0 ? unidadeFechadas / diasPassados : 0;
  const velNec   = diasRestantes > 0 && unidadeAbertos > 0 ? unidadeAbertos / diasRestantes : 0;
  const ratio    = velNec > 0 ? velAtual / velNec : unidadeAbertos === 0 ? 2 : 0;
  const status   = unidadeAbertos === 0 ? "concluido"
    : diasPassados === 0               ? "ok"
    : ratio >= 1.0                     ? "ok"
    : ratio >= 0.65                    ? "risco"
    : "atrasado";
  return { total, fechados, abertos, horasTotais, horasFechadas, horasAbertos, usarHoras, velAtual, velNec, ratio, status };
}

function fmt(n, dec = 1) {
  if (n == null) return "—";
  return Number(n).toFixed(dec);
}

function formatDate(str) {
  if (!str) return "—";
  const [d] = str.split(" ");
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
export default function SprintSaude() {
  const [tickets,   setTickets]   = useState([]);
  const [sprint,    setSprint]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  async function load() {
    try {
      const [tks, sp] = await Promise.all([
        getSprintDados(),
        getSetupSprint(),
      ]);
      setTickets(tks || []);
      setSprint(sp);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const saude = calcSaude(tickets, sprint);
  const cfg   = saude ? STATUS_CFG[saude.status] : null;

  /* Devs únicos com programados */
  const devNames = [...new Set(
    tickets.filter(isProg).map(t => t.desenvolvedor).filter(Boolean)
  )].sort();

  const devSaudes = devNames.map(nome => ({
    nome,
    ...calcDevSaude(
      tickets.filter(t => isProg(t) && t.desenvolvedor === nome),
      saude?.diasPassados ?? 0,
      saude?.diasRestantes ?? 0,
    ),
  })).sort((a, b) => {
    const order = { atrasado: 0, risco: 1, ok: 2, concluido: 3 };
    return (order[a.status] ?? 4) - (order[b.status] ?? 4);
  });

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", textAlign: "center", paddingTop: 60, fontSize: 13 }}>
        Carregando dados da sprint…
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Saúde da Sprint</h1>
          {sprint && (
            <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
              Versão {sprint.versao} · {formatDate(sprint.data_inicial)} → {formatDate(sprint.data_final)}
              {saude && (
                <span style={{ marginLeft: 10, color: "#334155" }}>
                  · {saude.diasPassados}d decorridos · {saude.diasRestantes}d restantes
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "#475569" }}>Atualiza em {countdown}s</span>
          <button
            onClick={() => { loadRef.current(); setCountdown(REFRESH_SEC); }}
            style={btnRefresh}
          >↻</button>
        </div>
      </header>

      {saude && cfg && (
        <>
          {/* ── Semáforo principal ── */}
          <div style={{
            ...semaforoCard,
            borderColor: `${cfg.color}30`,
            background: `linear-gradient(135deg, ${cfg.color}0f 0%, rgba(11,22,42,0.6) 100%)`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              {/* Pulso + ícone */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                {cfg.pulse && (
                  <div style={{
                    position: "absolute", inset: -8,
                    borderRadius: "50%",
                    background: `${cfg.color}20`,
                    animation: "saudePulse 1.8s ease-in-out infinite",
                  }} />
                )}
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: `${cfg.color}22`,
                  border: `2px solid ${cfg.color}60`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, color: cfg.color, fontWeight: 800,
                  boxShadow: `0 0 20px ${cfg.color}40`,
                }}>
                  {cfg.icon}
                </div>
              </div>

              {/* Status + projeção */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: cfg.color, letterSpacing: "0.05em", lineHeight: 1 }}>
                  {cfg.label}
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6 }}>
                  {saude.abertos === 0
                    ? "Todos os tickets foram concluídos."
                    : saude.diasParaConcluir == null
                      ? "Nenhum ticket fechado ainda — velocidade insuficiente para projetar."
                      : saude.diasAlem != null && saude.diasAlem <= 0
                        ? `Projeção: conclui em ${saude.diasParaConcluir} dia${saude.diasParaConcluir !== 1 ? "s" : ""} — ${Math.abs(saude.diasAlem)} dia${Math.abs(saude.diasAlem) !== 1 ? "s" : ""} de folga.`
                        : saude.diasAlem != null
                          ? `Projeção: conclui em ${saude.diasParaConcluir} dias — ${saude.diasAlem} dia${saude.diasAlem !== 1 ? "s" : ""} além do prazo.`
                          : `Projeção: conclui em ${saude.diasParaConcluir} dia${saude.diasParaConcluir !== 1 ? "s" : ""}.`
                  }
                </div>
              </div>

              {/* Ratio */}
              {saude.velNecessaria > 0 && (
                <RatioTooltip saude={saude} cfg={cfg} />
              )}
            </div>

            {/* Progresso dias + tickets + horas */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
              <ProgressBar
                label={`Tempo da sprint — dia ${saude.diasPassados} de ${saude.totalDias}`}
                pct={saude.pctDias}
                color="#60a5fa"
              />
              <ProgressBar
                label={`Tickets concluídos — ${saude.fechados} de ${saude.total}`}
                pct={saude.pctConc}
                color={cfg.color}
              />
              {saude.usarHoras && (
                <ProgressBar
                  label={`Horas estimadas — ${saude.horasFechadas}h de ${saude.horasTotais}h`}
                  pct={saude.pctHoras}
                  color="#a78bfa"
                />
              )}
            </div>
          </div>

          {/* ── KPI Cards ── */}
          <div style={kpiGrid}>
            <KpiCard label="Tickets Programados" value={saude.total}    color="#e2e8f0" />
            <KpiCard label="Fechados"            value={saude.fechados} color="#4ade80" />
            <KpiCard label="Em Aberto"           value={saude.abertos}  color={saude.abertos > 0 ? "#f87171" : "#4ade80"} />
            <KpiCard
              label="Velocidade Atual"
              value={saude.usarHoras ? `${fmt(saude.velAtual)}h/dia` : `${fmt(saude.velAtual)}/dia`}
              sub={saude.velNecessaria > 0
                ? `precisa ${fmt(saude.velNecessaria)}${saude.usarHoras ? "h" : ""}/dia`
                : "meta atingida"}
              color={cfg.color}
            />
          </div>

          {/* ── Velocidade: atual vs necessária ── */}
          {saude.velNecessaria > 0 && (
            <div style={glass}>
              <div style={sectionLabel}>Velocidade — Atual vs Necessária</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <VelBar
                  label="Velocidade atual"
                  val={saude.velAtual}
                  max={Math.max(saude.velAtual, saude.velNecessaria) * 1.2}
                  color={cfg.color}
                  unit={saude.usarHoras ? "h/d" : "/d"}
                />
                <VelBar
                  label="Velocidade necessária"
                  val={saude.velNecessaria}
                  max={Math.max(saude.velAtual, saude.velNecessaria) * 1.2}
                  color="#60a5fa"
                  dashed
                  unit={saude.usarHoras ? "h/d" : "/d"}
                />
              </div>
              <div style={{ fontSize: 11, color: "#334155", marginTop: 10 }}>
                {saude.usarHoras
                  ? "velocidade = horas estimadas (Size) fechadas por dia corrido desde o início da sprint"
                  : "velocidade = tickets fechados por dia corrido — tickets sem Size não possuem estimativa"}
              </div>
            </div>
          )}

          {/* ── Por Desenvolvedor ── */}
          {devSaudes.length > 0 && (
            <div>
              <div style={{ ...sectionLabel, marginBottom: 12 }}>Por Desenvolvedor</div>
              <div style={devGrid}>
                {devSaudes.map(d => {
                  const dc = STATUS_CFG[d.status];
                  const av = devAv(d.nome);
                  const pct = d.usarHoras && d.horasTotais > 0
                    ? Math.round(d.horasFechadas / d.horasTotais * 100)
                    : d.total > 0 ? Math.round(d.fechados / d.total * 100) : 0;
                  return (
                    <div key={d.nome} style={{ ...glass, borderLeft: `3px solid ${dc.color}40` }}>
                      {/* Header do card */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {devInitials(d.nome)}
                        </div>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{d.nome}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          {dc.pulse && (
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: dc.color, boxShadow: `0 0 6px ${dc.color}`, animation: "saudePulse 1.8s ease-in-out infinite" }} />
                          )}
                          <span style={{ fontSize: 11, fontWeight: 700, color: dc.color }}>{dc.label}</span>
                        </div>
                      </div>

                      {/* Contadores */}
                      <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: "#4ade80" }}>{d.fechados}</div>
                          <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Fechados</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: d.abertos > 0 ? "#f87171" : "#475569" }}>{d.abertos}</div>
                          <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Abertos</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: "#e2e8f0" }}>{d.total}</div>
                          <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total</div>
                        </div>
                        <div style={{ flex: 1 }} />
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: dc.color }}>{pct}%</div>
                          <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Concluído</div>
                        </div>
                      </div>

                      {/* Barra de progresso */}
                      <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 2, overflow: "hidden", marginBottom: 10 }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: dc.color, borderRadius: 2, transition: "width 0.6s" }} />
                      </div>

                      {/* Velocidades */}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                        <span style={{ color: "#475569" }}>
                          Atual: <span style={{ color: dc.color, fontWeight: 600 }}>{fmt(d.velAtual)}{d.usarHoras ? "h" : ""}/dia</span>
                        </span>
                        {d.velNec > 0 && (
                          <span style={{ color: "#475569" }}>
                            Precisa: <span style={{ color: "#60a5fa", fontWeight: 600 }}>{fmt(d.velNec)}{d.usarHoras ? "h" : ""}/dia</span>
                          </span>
                        )}
                      </div>
                      {d.usarHoras && (
                        <div style={{ fontSize: 10, color: "#334155", marginTop: 4 }}>
                          {d.horasFechadas}h fechadas · {d.horasAbertos}h abertas · {d.horasTotais}h total
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {!saude && !loading && (
        <div style={{ color: "#475569", textAlign: "center", paddingTop: 40, fontSize: 13 }}>
          Sem dados de sprint configurados. Acesse Programação → Setup para configurar.
        </div>
      )}

      <style>{`
        @keyframes saudePulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}

/* ── Sub-components ── */

function RatioTooltip({ saude, cfg }) {
  const [show, setShow]     = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const triggerRef          = useRef(null);
  const pct = Math.round(saude.ratio * 100);

  function handleEnter() {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.top + r.height / 2, right: window.innerWidth - r.left + 14 });
    }
    setShow(true);
  }

  const tooltipContent = (
    <div style={{
      position: "fixed",
      top: coords.top,
      right: coords.right,
      transform: "translateY(-50%)",
      width: 252,
      background: "rgba(8,15,32,0.98)",
      border: "1px solid rgba(96,165,250,0.2)",
      borderRadius: 12,
      padding: "14px 16px",
      zIndex: 9999,
      textAlign: "left",
      boxShadow: "0 12px 40px rgba(0,0,0,0.7)",
      pointerEvents: "none",
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: "#64748b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Como é calculado
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4, lineHeight: 1.5 }}>
        velocidade atual ÷ velocidade necessária
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, lineHeight: 1.6 }}>
        <span style={{ color: cfg.color }}>{fmt(saude.velAtual)}{saude.usarHoras ? "h" : ""}/dia</span>
        <span style={{ color: "#334155" }}> ÷ </span>
        <span style={{ color: "#60a5fa" }}>{fmt(saude.velNecessaria)}{saude.usarHoras ? "h" : ""}/dia</span>
        <span style={{ color: "#334155" }}> = </span>
        <span style={{ color: cfg.color }}>{pct}%</span>
      </div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 12, lineHeight: 1.6, borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 10 }}>
        {saude.usarHoras ? (
          <>
            <div><span style={{ color: cfg.color }}>Atual</span> = horas estimadas fechadas ÷ dias decorridos</div>
            <div><span style={{ color: "#60a5fa" }}>Necessária</span> = horas estimadas abertas ÷ dias restantes</div>
            <div style={{ marginTop: 4, color: "#334155" }}>Estimativa: P=1h · M=9h · G=18h · XG=36h</div>
          </>
        ) : (
          <>
            <div><span style={{ color: cfg.color }}>Atual</span> = tickets fechados ÷ dias decorridos</div>
            <div><span style={{ color: "#60a5fa" }}>Necessária</span> = tickets em aberto ÷ dias restantes</div>
            <div style={{ marginTop: 4, color: "#334155" }}>Sem Size definido — usando contagem de tickets</div>
          </>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <span style={{ color: "#4ade80", fontWeight: 700, width: 52, flexShrink: 0 }}>≥ 100%</span>
          <span style={{ color: "#64748b" }}>no prazo — ritmo suficiente</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <span style={{ color: "#fbbf24", fontWeight: 700, width: 52, flexShrink: 0 }}>65–99%</span>
          <span style={{ color: "#64748b" }}>em risco — pode atrasar</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <span style={{ color: "#f87171", fontWeight: 700, width: 52, flexShrink: 0 }}>{"< 65%"}</span>
          <span style={{ color: "#64748b" }}>atrasado — ritmo insuficiente</span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      ref={triggerRef}
      style={{ textAlign: "center", flexShrink: 0, cursor: "default" }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      <div style={{ fontSize: 32, fontWeight: 900, color: cfg.color, lineHeight: 1 }}>
        {pct}%
      </div>
      <div style={{ fontSize: 10, color: "#475569", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.07em", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
        capacidade
        <span style={{ fontSize: 9, color: "#334155", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, fontWeight: 700 }}>?</span>
      </div>
      {show && createPortal(tooltipContent, document.body)}
    </div>
  );
}

function ProgressBar({ label, pct, color }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#64748b" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, borderRadius: 3, transition: "width 0.6s" }} />
      </div>
    </div>
  );
}

function VelBar({ label, val, max, color, dashed, unit = "/d" }) {
  const pct = max > 0 ? Math.min(100, Math.round(val / max * 100)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 12, color: "#64748b", width: 220, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: dashed
            ? `repeating-linear-gradient(90deg, ${color} 0px, ${color} 6px, transparent 6px, transparent 12px)`
            : color,
          borderRadius: 3, transition: "width 0.6s",
        }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, width: 64, textAlign: "right", flexShrink: 0 }}>
        {Number(val).toFixed(1)}{unit}
      </span>
    </div>
  );
}

function KpiCard({ label, value, sub, color }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{ ...glass, borderLeft: `3px solid ${color}`, borderRadius: "0 10px 10px 0", background: hov ? "rgba(255,255,255,0.04)" : glass.background, transition: "background 0.2s" }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
    >
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ── Styles ── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9" };

const header = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", marginBottom: 24, gap: 12,
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  marginBottom: 16,
};

const semaforoCard = {
  border: "1px solid",
  borderRadius: 16,
  padding: "24px 28px",
  marginBottom: 16,
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
};

const kpiGrid = {
  display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16,
};

const devGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 14,
  marginBottom: 24,
};

const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em",
};
